import { useState } from 'react';
import { useBoletos } from '@/features/financeiro/hooks/useBoletos';
import type { OrdenarPorBoletos } from '@/features/financeiro/services/boletosService';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import type { BoletoResumo, BaixarBoletoRequest } from '@/features/financeiro/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Download, XCircle, Printer, ChevronLeft, ChevronRight, Search, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { sicoobProvider } from '@/features/bancoIntegracao/providers/sicoobProvider';
import { PessoaSearchCombobox } from '@/components/PessoaSearchCombobox';
import { useDebounce } from '@/hooks/useDebounce';
import { getApiErrorMessage } from '@/lib/apiClient';
import { EmitirBoletoContasReceberDialog } from '@/features/financeiro/components/EmitirBoletoContasReceberDialog';

const PAGE_SIZES = [10, 20, 50] as const;

/// Boleto registrado pela API de um banco: ações vão ao banco, não à base local.
const ehDoBanco = (b: BoletoResumo) => b.origem === 'SICOOB';

function statusBoleto(b: BoletoResumo) {
  if (ehDoBanco(b)) {
    if (b.isPago || b.situacao === 'LIQUIDADO') return { label: 'Pago', variant: 'default' as const };
    if (b.situacao === 'BAIXADO')               return { label: 'Cancelado no banco', variant: 'destructive' as const };
    if (b.situacao === 'BAIXA_SOLICITADA')      return { label: 'Cancelamento solicitado', variant: 'outline' as const };
  }
  if (b.isEstornado)         return { label: 'Estornado',  variant: 'outline'       as const };
  if (b.dtCancelamento)      return { label: 'Cancelado',  variant: 'destructive'   as const };
  if (b.isPago)              return { label: 'Pago',       variant: 'default'        as const };
  const venc = new Date(b.dtVencimento);
  if (venc < new Date())     return { label: 'Vencido',    variant: 'destructive'   as const };
  return                            { label: 'A Vencer',   variant: 'secondary'     as const };
}

function fmt(v: number) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function fmtDate(s: string | null | undefined) {
  if (!s) return '—';
  return format(new Date(s), 'dd/MM/yyyy', { locale: ptBR });
}

// ── Dialogs ───────────────────────────────────────────────────────────────

interface BaixarDialogProps {
  boleto: BoletoResumo | null;
  onClose: () => void;
  onConfirm: (req: BaixarBoletoRequest) => Promise<void>;
  isLoading: boolean;
}

function BaixarDialog({ boleto, onClose, onConfirm, isLoading }: BaixarDialogProps) {
  const [dtBaixa,       setDtBaixa]       = useState(format(new Date(), 'yyyy-MM-dd'));
  const [valorRecebido, setValorRecebido] = useState(boleto?.valorTotal.toFixed(2) ?? '');
  const [valorDesc,     setValorDesc]     = useState('0');
  const [valorAcres,    setValorAcres]    = useState('0');

  const handleConfirm = async () => {
    await onConfirm({
      dtBaixa,
      valorRecebido: parseFloat(valorRecebido),
      valorDesconto:  parseFloat(valorDesc)  || 0,
      valorAcrescimo: parseFloat(valorAcres) || 0,
    });
    onClose();
  };

  return (
    <Dialog open={!!boleto} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Baixar Boleto {boleto?.nossoNumero ? `nº ${boleto.nossoNumero}` : ""}{boleto?.clienteNome ? ` — ${boleto.clienteNome}` : ""}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="space-y-1">
            <Label>Data da Baixa *</Label>
            <Input type="date" value={dtBaixa} onChange={e => setDtBaixa(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Valor Recebido *</Label>
            <Input type="number" step="0.01" value={valorRecebido} onChange={e => setValorRecebido(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label>Desconto</Label>
              <Input type="number" step="0.01" value={valorDesc} onChange={e => setValorDesc(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Acréscimo</Label>
              <Input type="number" step="0.01" value={valorAcres} onChange={e => setValorAcres(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? 'Processando...' : 'Confirmar Baixa'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Página Principal ──────────────────────────────────────────────────────

export default function GestaoBoletos() {
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const [filtroStatus,  setFiltroStatus]  = useState<string>('todos');
  // Período pelo VENCIMENTO; abre no mês corrente inteiro (até o último dia), porque
  // boleto vence adiante. Ordem padrão: vencimento crescente — o próximo a vencer
  // (ou o mais atrasado) no topo, como no Contas a Receber.
  const periodo   = usePeriodo('mes', () => setPagina(1), { mesInteiro: true });
  const ordenacao = useOrdenacao<OrdenarPorBoletos>('vencimento', false, () => setPagina(1));
  const [clienteId,    setClienteId]    = useState(0);
  const [busca,        setBusca]        = useState('');
  const buscaAplicada = useDebounce(busca.trim(), 400);

  const isPago = filtroStatus === 'pagos' ? true
               : filtroStatus === 'pendentes' ? false
               : undefined;

  const { boletos, total, totalPaginas, isLoading, isFetching, baixar, isBaixando, cancelar } =
    useBoletos({
      isPago,
      ...periodo.intervalo,
      ordenarPor:  ordenacao.ordenarPor,
      decrescente: ordenacao.decrescente,
      clienteId: clienteId || undefined,
      busca:    buscaAplicada || undefined,
      pagina,
      tamanhoPagina,
    });

  const firstItem = total === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const lastItem  = Math.min(pagina * tamanhoPagina, total);
  const handlePageSizeChange = (value: string) => { setTamanhoPagina(Number(value)); setPagina(1); };

  const [emitirOpen,      setEmitirOpen]      = useState(false);
  const [baixarBoleto,    setBaixarBoleto]    = useState<BoletoResumo | null>(null);
  const [cancelarBoleto,  setCancelarBoleto]  = useState<BoletoResumo | null>(null);
  const queryClient = useQueryClient();

  const handleBaixar = async (req: BaixarBoletoRequest) => {
    if (!baixarBoleto) return;
    try {
      await baixar({ id: baixarBoleto.id, req });
      toast.success('Boleto baixado com sucesso.');
    } catch {
      toast.error('Erro ao baixar boleto.');
    }
  };

  const handleCancelar = async () => {
    const b = cancelarBoleto;
    if (!b) return;
    try {
      if (ehDoBanco(b)) {
        // Pede a baixa no Sicoob; o banco processa com atraso (fica "solicitado").
        await sicoobProvider.cancelarCobranca(b.contaCorrenteId ?? 0, b.nossoNumero ?? '', '');
        await queryClient.invalidateQueries({ queryKey: ['boletos'] });
        toast.success('Cancelamento pedido ao Sicoob. O banco processa em instantes.');
      } else {
        await cancelar(b.id);
        toast.success('Boleto cancelado.');
      }
    } catch (e) {
      toast.error('Erro ao cancelar boleto.', { description: getApiErrorMessage(e) });
    } finally {
      setCancelarBoleto(null);
    }
  };

  const handleSegundaVia = async (b: BoletoResumo) => {
    try {
      const blob = await sicoobProvider.baixarCobrancaPdf(b.contaCorrenteId ?? 0, b.nossoNumero ?? '');
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error('Erro ao obter a 2ª via.', { description: getApiErrorMessage(e) });
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Gestão de Boletos</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Emissão, baixa e controle de boletos bancários
          </p>
        </div>
        <Button onClick={() => setEmitirOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Emitir Boleto
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="space-y-1">
          <Label className="text-xs">Status</Label>
          <Select value={filtroStatus} onValueChange={v => { setFiltroStatus(v); setPagina(1); }}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="pendentes">Pendentes</SelectItem>
              <SelectItem value="pagos">Pagos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Vencimento</Label>
          <div className="flex flex-wrap gap-2">
            <PeriodoFiltro estado={periodo} rotuloData="Vencimento" />
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Cliente</Label>
          <div className="flex gap-1 w-72">
            <div className="flex-1">
              <PessoaSearchCombobox
                filter="cliente"
                value={clienteId || undefined}
                onChange={id => { setClienteId(id); setPagina(1); }}
                placeholder="Todos os clientes"
              />
            </div>
            {clienteId > 0 && (
              <Button variant="ghost" size="icon" title="Limpar cliente" onClick={() => { setClienteId(0); setPagina(1); }}>
                <XCircle className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Buscar</Label>
          <Input
            className="w-64"
            value={busca}
            onChange={e => { setBusca(e.target.value); setPagina(1); }}
            placeholder="Nosso número, linha digitável, cliente"
          />
        </div>
        <Button variant="outline" size="icon" title="Limpar filtros"
          onClick={() => { setFiltroStatus('todos'); periodo.setPeriodo('mes'); setClienteId(0); setBusca(''); setPagina(1); }}>
          <Search className="h-4 w-4" />
        </Button>
      </div>

      {/* Total */}
      <p className="text-sm text-muted-foreground">{total} boleto(s) encontrado(s)</p>

      {/* Tabela */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cliente</TableHead>
              <CabecalhoOrdenavel coluna="vencimento" ordenacao={ordenacao} decrescentePadrao={false}>Vencimento</CabecalhoOrdenavel>
              <CabecalhoOrdenavel coluna="valor" ordenacao={ordenacao}>Valor</CabecalhoOrdenavel>
              <TableHead>Nosso Número</TableHead>
              <TableHead>Status</TableHead>
              <CabecalhoOrdenavel coluna="baixa" ordenacao={ordenacao}>Pago em</CabecalhoOrdenavel>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              : boletos.length === 0
              ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                      Nenhum boleto encontrado.
                    </TableCell>
                  </TableRow>
                )
              : boletos.map(b => {
                  const st = statusBoleto(b);
                  const doBanco = ehDoBanco(b);
                  // Boleto do banco: pagamento chega pela liquidação; baixa manual
                  // deixaria o boleto pagável no banco.
                  const podeBaixar   = !doBanco && !b.isPago && !b.dtCancelamento && !b.isEstornado;
                  const podeCancelar = doBanco
                    ? !b.isPago && (b.situacao ?? 'EM_ABERTO') === 'EM_ABERTO'
                    : !b.isPago && !b.dtCancelamento;
                  return (
                    <TableRow key={`${b.origem ?? 'LOCAL'}-${b.id}`}>
                      <TableCell className="max-w-[240px] truncate" title={b.clienteNome ?? undefined}>
                        {b.clienteNome || `Cliente cód. ${b.clienteId}`}
                      </TableCell>
                      <TableCell>{fmtDate(b.dtVencimento)}</TableCell>
                      <TableCell className="font-medium">{fmt(b.valorTotal)}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {b.nossoNumero ?? '—'}
                        {doBanco && <Badge variant="outline" className="ml-2 font-sans">Sicoob</Badge>}
                      </TableCell>
                      <TableCell>
                        <Badge variant={st.variant}>{st.label}</Badge>
                      </TableCell>
                      <TableCell>{fmtDate(b.dtBaixa)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          {doBanco && (
                            <Button size="sm" variant="outline" onClick={() => handleSegundaVia(b)} title="2ª via (PDF do banco)">
                              <FileText className="h-3 w-3 mr-1" />
                              2ª via
                            </Button>
                          )}
                          {podeBaixar && (
                            <Button
                              size="sm" variant="outline"
                              onClick={() => setBaixarBoleto(b)}
                              title="Baixar boleto"
                            >
                              <Download className="h-3 w-3 mr-1" />
                              Baixar
                            </Button>
                          )}
                          {podeCancelar && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => setCancelarBoleto(b)}
                              title={doBanco ? 'Cancelar no banco' : 'Cancelar boleto'}
                              className="text-destructive hover:text-destructive"
                            >
                              <XCircle className="h-3 w-3" />
                            </Button>
                          )}
                          {b.isImpresso && (
                            <Printer className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
            }
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
        <p className="text-sm text-muted-foreground">
          {total === 0 ? 'Nenhum registro'
            : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
          {isFetching && !isLoading && <span className="ml-2 text-xs">(atualizando...)</span>}
        </p>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
            <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
              <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(1)} disabled={pagina === 1}>«</Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1}><ChevronLeft className="h-4 w-4" /></Button>
            {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
              let page: number;
              if      (totalPaginas <= 5)         page = i + 1;
              else if (pagina <= 3)                page = i + 1;
              else if (pagina >= totalPaginas - 2) page = totalPaginas - 4 + i;
              else                                 page = pagina - 2 + i;
              return (
                <Button key={page} variant={pagina === page ? 'default' : 'outline'}
                  size="icon" className="h-8 w-8 text-xs" onClick={() => setPagina(page)}>{page}</Button>
              );
            })}
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas}>»</Button>
          </div>
          <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pagina} de {totalPaginas}</span>
        </div>
      </div>

      {/* Dialogs */}
      <EmitirBoletoContasReceberDialog
        open={emitirOpen}
        onClose={() => setEmitirOpen(false)}
        onEmitidos={() => queryClient.invalidateQueries({ queryKey: ['boletos'] })}
      />

      <BaixarDialog
        boleto={baixarBoleto}
        onClose={() => setBaixarBoleto(null)}
        onConfirm={handleBaixar}
        isLoading={isBaixando}
      />

      <AlertDialog open={!!cancelarBoleto} onOpenChange={v => { if (!v) setCancelarBoleto(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar Boleto</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelarBoleto && ehDoBanco(cancelarBoleto)
                ? `O boleto ${cancelarBoleto.nossoNumero} será baixado (cancelado) no Sicoob e não poderá mais ser pago. Esta ação não pode ser desfeita.`
                : `Tem certeza que deseja cancelar o boleto${cancelarBoleto?.nossoNumero ? ` nº ${cancelarBoleto.nossoNumero}` : ""}${cancelarBoleto?.clienteNome ? ` de ${cancelarBoleto.clienteNome}` : ""}? Esta ação não pode ser desfeita.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleCancelar}
            >
              Cancelar Boleto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
